// Every user's task groups: name and source, owner, task type, the pinned workflow version, status,
// task counts by status and the poller's last word; a group with its own VM is badged by its name.
import { Cpu, Cube, WarningCircle } from "@phosphor-icons/react";
import { Link } from "react-router";

import { ago, shortId } from "../../pages/format";
import { type Column, DataTable } from "../../ui/DataTable";
import { Mono, Pill, StatusPill } from "../../ui/status";
import { statusLabel, type Tone } from "../../ui/tones";
import { type Messages, useT } from "../../i18n";
import { GROUP_TONE, sourceDetail, type StatusCounts, TASK_TONE } from "../display";
import type { Lookups } from "../lookups";
import { type Isolation, TASK_STATUSES, type TaskGroup } from "../model";

export function IsolationBadge({ isolation }: { isolation: Isolation }) {
  const t = useT().tasks;
  return isolation === "vm" ? (
    <Pill tone="primary" icon={<Cpu weight="bold" />}>
      {t.ownVm}
    </Pill>
  ) : (
    <Pill tone="gray" icon={<Cube weight="bold" />}>
      {t.container}
    </Pill>
  );
}

export function WorkflowVersion({ lookups, versionId }: { lookups: Lookups; versionId: string }) {
  const v = lookups.version(versionId);
  if (!v) {
    return (
      <span title={versionId}>
        <Mono>{shortId(versionId)}</Mono>
      </span>
    );
  }
  return (
    <span className="whitespace-nowrap">
      <Link to={`/workflows/${v.workflowId}`} className="text-gray-900 hover:text-primary">
        {v.workflowName}
      </Link>{" "}
      <Mono className="text-gray-500">v{v.version}</Mono>
    </span>
  );
}

const SEGMENT: Record<Tone, string> = {
  green: "bg-green",
  orange: "bg-orange",
  red: "bg-red",
  blue: "bg-blue",
  gray: "bg-gray-400",
  primary: "bg-primary",
  secondary: "bg-secondary",
};

/** The total and a bar of the counts by status, in the machine's order; the words in its label. */
export function TaskCounts({ counts }: { counts: StatusCounts | undefined }) {
  const t = useT().tasks.group;
  if (!counts) return <span className="text-gray-400">…</span>;
  const shown = TASK_STATUSES.filter((s) => counts[s] > 0);
  const total = shown.reduce((n, s) => n + counts[s], 0);
  if (total === 0) return <span className="text-gray-500">{t.noTasks}</span>;
  const words = shown.map((s) => `${counts[s]} ${statusLabel(s)}`).join(", ");
  const active = counts.running + counts.waiting_for_human;
  return (
    <span className="block w-32" title={words} aria-label={t.countsLabel(total, words)}>
      <span className="tabular text-[13px] font-semibold text-gray-900">{total}</span>
      <span className="text-xs text-gray-500">
        {t.tasksWord(total)}
        {active > 0 ? t.activeCount(active) : ""}
      </span>
      <span className="mt-1 flex h-1.5 gap-px overflow-hidden rounded-full bg-gray-100" aria-hidden>
        {shown.map((s) => (
          <span key={s} className={SEGMENT[TASK_TONE[s]]} style={{ width: `${(counts[s] / total) * 100}%` }} />
        ))}
      </span>
    </span>
  );
}

export function PollState({ group }: { group: TaskGroup }) {
  const t = useT().tasks;
  if (group.source === "manual") return <span className="text-gray-500">{t.source.manual}</span>;
  if (group.last_poll_error) {
    return (
      <span className="inline-flex items-center gap-1.5 text-red-dark" title={group.last_poll_error}>
        <WarningCircle weight="fill" className="h-4 w-4 shrink-0 text-red" />
        <span className="max-w-[11rem] truncate">{group.last_poll_error}</span>
      </span>
    );
  }
  return <span className="whitespace-nowrap">{group.last_polled_at ? ago(group.last_polled_at) : t.group.notYet}</span>;
}

function columns(t: Messages["tasks"], lookups: Lookups, counts: (id: string) => StatusCounts | undefined): Column<TaskGroup>[] {
  const c = t.columns;
  return [
    {
      key: "name",
      header: c.taskGroup,
      sortValue: (g) => g.name.toLowerCase(),
      cell: (g) => (
        <div className="min-w-0">
          <span className="flex items-center gap-2">
            <Link to={`/task-groups/${g.id}`} className="truncate font-semibold text-gray-900 hover:text-primary">
              {g.name}
            </Link>
            {g.isolation === "vm" ? <IsolationBadge isolation="vm" /> : null}
          </span>
          <span className="block max-w-[18rem] truncate text-xs text-gray-500">
            {t.source[g.source]}
            {sourceDetail(g) ? ` · ${sourceDetail(g)}` : ""}
          </span>
        </div>
      ),
    },
    {
      key: "owner",
      header: c.owner,
      hideBelow: "md",
      sortValue: (g) => lookups.owner(g.owner_user_id),
      cell: (g) => <span className="whitespace-nowrap">{lookups.owner(g.owner_user_id)}</span>,
    },
    { key: "type", header: c.taskType, hideBelow: "2xl", cell: (g) => lookups.taskType(g.task_type_id) },
    { key: "workflow", header: c.workflow, hideBelow: "lg", cell: (g) => <WorkflowVersion lookups={lookups} versionId={g.workflow_version_id} /> },
    { key: "status", header: c.status, sortValue: (g) => g.status, cell: (g) => <StatusPill tone={GROUP_TONE[g.status]} status={g.status} /> },
    { key: "tasks", header: c.tasks, hideBelow: "sm", cell: (g) => <TaskCounts counts={counts(g.id)} /> },
    { key: "polled", header: c.lastPolled, hideBelow: "xl", cell: (g) => <PollState group={g} /> },
  ];
}

export function TaskGroupsTable({
  rows,
  lookups,
  counts,
}: {
  rows: TaskGroup[];
  lookups: Lookups;
  counts: (groupId: string) => StatusCounts | undefined;
}) {
  const t = useT().tasks;
  return <DataTable label={t.group.tableLabel} columns={columns(t, lookups, counts)} rows={rows} rowKey={(g) => g.id} />;
}
